import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { FiLock } from 'react-icons/fi';

import s from './Breadcrumb.module.scss';
import { BreadcrumbSwitcher, type BreadcrumbSibling } from './BreadcrumbSwitcher';

export type { BreadcrumbSibling };

export interface BreadcrumbItem {
  label: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
  /**
   * The owner's plan does not carry this destination, but a bigger one would. Drawn with a
   * padlock and still followable — it lands on the placeholder that explains the plan, the
   * same thing the nav's padlocked rows do. Only ever set for the owner: for everyone else a
   * withheld destination reads `hidden`, and `useBreadcrumbGate` takes the click off the
   * crumb — or drops the switcher row — instead.
   */
  locked?: boolean;
}

interface Props {
  items: BreadcrumbItem[];
  siblings?: BreadcrumbSibling[];
  parent?: BreadcrumbSibling;
  separator?: ReactNode;
  className?: string;
  /**
   * When the whole trail does not fit, take the last crumb out of it instead of
   * squeezing it: the trail keeps its ancestors and `onLeafWrapChange(true)` tells
   * the owner to draw the leaf on a row of its own (see `BreadcrumbLeaf`).
   */
  wrapLeaf?: boolean;
  onLeafWrapChange?: (wrapped: boolean) => void;
}

const ELLIPSIS = '…';

/** Chevron (0.7em of the 16–24px crumb) plus its 8px gap, drawn only on a switcher. */
const SWITCHER_EXTRA = 26;

const LOCK_TITLE = 'Not on your plan';

const renderLock = () => <FiLock className={s.lock} aria-label={LOCK_TITLE} />;

const renderPlainItem = (item: BreadcrumbItem, key: string | number) =>
  item.onClick ? (
    <button
      key={key}
      className={clsx(s.link, item.locked && s.linkLocked)}
      onClick={item.onClick}
      title={item.locked ? LOCK_TITLE : undefined}
    >
      {item.icon && <span className={s.icon}>{item.icon}</span>}
      <span className={s.label}>{item.label}</span>
      {item.locked && renderLock()}
    </button>
  ) : (
    <span key={key} className={s.item} title={item.locked ? LOCK_TITLE : undefined}>
      {item.icon && <span className={s.icon}>{item.icon}</span>}
      <span className={s.label}>{item.label}</span>
      {item.locked && renderLock()}
    </span>
  );

export const Breadcrumb = ({
  items,
  siblings,
  parent,
  separator = '›',
  className,
  wrapLeaf = false,
  onLeafWrapChange,
}: Props) => {
  const hasSwitcher = !!siblings?.length;
  const containerRef = useRef<HTMLDivElement>(null);
  const measurerRef = useRef<HTMLDivElement>(null);

  // How many of the middle ancestors (everything except first + last) to show.
  // null = not measured yet → render all. A count (not indices) stays valid
  // across item changes once clamped, so it can never duplicate/reorder items.
  const [keptCount, setKeptCount] = useState<number | null>(null);
  // The leaf has moved out to the owner's own row (`wrapLeaf` only).
  const [leafWrapped, setLeafWrapped] = useState(false);
  const isLeafWrapped = wrapLeaf && leafWrapped;
  // With the leaf moved out, even `first › last-ancestor` can overflow a narrow
  // first row; the first then folds into the ellipsis too (`… › Logs`).
  const [dropFirst, setDropFirst] = useState(false);

  useEffect(() => {
    onLeafWrapChange?.(isLeafWrapped);
  }, [isLeafWrapped, onLeafWrapChange]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurer = measurerRef.current;
    if (!container || !measurer) return;

    const recompute = () => {
      const total = items.length;
      if (total <= 2 && !wrapLeaf) {
        setKeptCount((prev) => (prev === null ? prev : null));
        return;
      }

      const row = measurer.firstElementChild as HTMLElement | null;
      if (!row) return;
      // [ellipsis, item0, item1, …] — natural widths, never clipped/shrunk.
      const nodes = Array.from(row.children) as HTMLElement[];
      const ellipsisW = nodes[0]?.getBoundingClientRect().width ?? 0;
      const itemW = items.map((_, i) => nodes[i + 1]?.getBoundingClientRect().width ?? 0);
      // The measurer draws the last crumb plain; as a switcher it also carries
      // the chevron and its gap.
      if (hasSwitcher && total > 0) itemW[total - 1] += SWITCHER_EXTRA;

      const styles = getComputedStyle(container);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      // Per extra slot: leading separator + two gaps. The separator glyph isn't
      // measured directly, so 2 gaps acts as a small safety margin.
      const slotCost = (w: number) => w + gap * 2;
      const available = container.clientWidth - 8;

      // Does the whole trail fit as it is? If not, and the owner can take the
      // leaf, the trail continues with its ancestors only.
      const fullWidth = itemW.reduce((acc, w, i) => acc + (i === 0 ? w : slotCost(w)), 0);
      const wrapped = wrapLeaf && total >= 2 && fullWidth > available;
      setLeafWrapped((prev) => (prev === wrapped ? prev : wrapped));

      const n = wrapped ? total - 1 : total;

      // first + last are mandatory — but in a wrapped trail the first may fold
      // into the ellipsis when there is no room for both.
      const base = itemW[0] + slotCost(itemW[n - 1]);
      const drop = wrapped && n >= 2 && base + (n > 2 ? slotCost(ellipsisW) : 0) > available;
      setDropFirst((prev) => (prev === drop ? prev : drop));
      if (drop) {
        setKeptCount((prev) => (prev === 0 ? prev : 0));
        return;
      }

      if (n <= 2) {
        setKeptCount((prev) => (prev === null ? prev : null));
        return;
      }

      // Everything fits → keep all middle items, no ellipsis.
      const middle = Array.from({ length: n - 2 }, (_, k) => k + 1);
      const allWidth = middle.reduce((acc, i) => acc + slotCost(itemW[i]), base);
      if (allWidth <= available) {
        const next = n - 2;
        setKeptCount((prev) => (prev === next ? prev : next));
        return;
      }

      // Reserve the ellipsis, then add ancestors nearest the last item until full.
      let used = base + slotCost(ellipsisW);
      let count = 0;
      for (let i = n - 2; i >= 1; i--) {
        const w = used + slotCost(itemW[i]);
        if (w > available) break;
        used = w;
        count++;
      }
      setKeptCount((prev) => (prev === count ? prev : count));
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [items, hasSwitcher, wrapLeaf]);

  // With the leaf on its own row, the trail here is its ancestors alone.
  const shown = isLeafWrapped ? items.slice(0, -1) : items;
  const n = shown.length;
  const isDropFirst = isLeafWrapped && dropFirst && n >= 2;
  const showAll = !isDropFirst && (n <= 2 || keptCount === null || keptCount >= n - 2);

  const renderItem = (item: BreadcrumbItem, key: string | number) =>
    hasSwitcher && key === items.length - 1 ? (
      <BreadcrumbSwitcher
        key={key}
        label={item.label}
        icon={item.icon}
        locked={item.locked}
        siblings={siblings!}
        parent={parent}
      />
    ) : (
      renderPlainItem(item, key)
    );

  const renderSegment = (node: ReactNode, key: string, withSeparator: boolean) => (
    <span key={key} className={s.segment}>
      {withSeparator && <span className={s.separator}>{separator}</span>}
      {node}
    </span>
  );

  let content: ReactNode;
  if (showAll) {
    content = shown.map((item, i) => renderSegment(renderItem(item, i), `i-${i}`, i > 0));
  } else {
    // Keep `keptCount` ancestors nearest the last item; collapse the rest to "…".
    const count = Math.max(0, Math.min(keptCount ?? 0, n - 2));
    const firstKept = n - 1 - count; // index of first kept ancestor
    const segments: ReactNode[] = [];
    if (!isDropFirst) segments.push(renderSegment(renderItem(shown[0], 0), 'i-0', false));
    segments.push(
      renderSegment(<span className={s.ellipsis}>{ELLIPSIS}</span>, 'ellipsis', !isDropFirst),
    );
    for (let i = firstKept; i <= n - 1; i++) {
      segments.push(renderSegment(renderItem(shown[i], i), `i-${i}`, true));
    }
    content = segments;
  }

  return (
    <div ref={containerRef} className={clsx(s.breadcrumb, className)}>
      {content}

      {/* Hidden measurer (clipped to 0×0 so it never affects layout/scroll).
          Inner row: ellipsis token first, then every item at natural width. */}
      <div ref={measurerRef} className={s.measurer} aria-hidden>
        <div className={s.measurerRow}>
          <span className={s.ellipsis}>{ELLIPSIS}</span>
          {items.map((item, i) => (
            <span key={i}>{renderPlainItem(item, i)}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

interface LeafProps {
  item: BreadcrumbItem;
  siblings?: BreadcrumbSibling[];
  parent?: BreadcrumbSibling;
  className?: string;
}

/**
 * The trail's last crumb on a row of its own — what the owner of a `wrapLeaf`
 * breadcrumb draws once the trail reports it no longer fits. Same crumb, same
 * switcher; only the room around it is new, so the name is never cut short.
 */
export const BreadcrumbLeaf = ({ item, siblings, parent, className }: LeafProps) => (
  <div className={clsx(s.breadcrumb, s.leafRow, className)}>
    {siblings?.length ? (
      <BreadcrumbSwitcher
        label={item.label}
        icon={item.icon}
        locked={item.locked}
        siblings={siblings}
        parent={parent}
      />
    ) : (
      renderPlainItem(item, 'leaf')
    )}
  </div>
);
